import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Folder, AlertCircle, Loader2 } from 'lucide-react';
import { apiService } from '../services/api';
import { CreateFolderRequest } from '../../../shared/types';

interface FolderCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const FolderCreationModal: React.FC<FolderCreationModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [concertName, setConcertName] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const createFolderMutation = useMutation({
    mutationFn: (request: CreateFolderRequest) => apiService.createFolder(request),
    onSuccess: () => {
      // Refresh the folders list
      queryClient.invalidateQueries({ queryKey: ['folders'] });
      
      // Reset form and close modal
      setConcertName('');
      setValidationError(null);
      onClose();
      
      // Call success callback if provided
      onSuccess?.();
    },
    onError: (error: any) => {
      // Handle API errors
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create folder';
      setValidationError(errorMessage);
    }
  });

  const validateConcertName = (name: string): string | null => {
    if (!name.trim()) {
      return 'Concert name is required';
    }

    // Check minimum length
    if (name.trim().length < 2) {
      return 'Concert name must be at least 2 characters long';
    }

    // Check maximum length
    if (name.trim().length > 100) {
      return 'Concert name must be less than 100 characters';
    }

    // Check for invalid characters (basic validation for folder names)
    const invalidChars = /[<>:"/\\|?*\x00-\x1f]/;
    if (invalidChars.test(name)) {
      return 'Concert name contains invalid characters';
    }

    // Check for reserved names
    const reservedNames = ['con', 'prn', 'aux', 'nul', 'com1', 'com2', 'com3', 'com4', 'com5', 'com6', 'com7', 'com8', 'com9', 'lpt1', 'lpt2', 'lpt3', 'lpt4', 'lpt5', 'lpt6', 'lpt7', 'lpt8', 'lpt9'];
    if (reservedNames.includes(name.toLowerCase().trim())) {
      return 'Concert name cannot be a reserved system name';
    }

    // Check for leading/trailing dots or spaces
    if (name.startsWith('.') || name.endsWith('.') || name.startsWith(' ') || name.endsWith(' ')) {
      return 'Concert name cannot start or end with dots or spaces';
    }

    return null;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous validation errors
    setValidationError(null);
    
    // Validate concert name
    const trimmedName = concertName.trim();
    const validationError = validateConcertName(trimmedName);
    
    if (validationError) {
      setValidationError(validationError);
      return;
    }

    // Submit the request
    createFolderMutation.mutate({
      concertName: trimmedName
    });
  };

  const handleClose = () => {
    if (!createFolderMutation.isPending) {
      setConcertName('');
      setValidationError(null);
      onClose();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setConcertName(value);
    
    // Clear validation error when user starts typing
    if (validationError) {
      setValidationError(null);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Folder className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Create New Folder
                </h3>
                <p className="text-sm text-gray-500">
                  Create a new folder in the Upload Tier for organizing your audio files
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={createFolderMutation.isPending}
              className="text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 disabled:opacity-50"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="concertName" className="block text-sm font-medium text-gray-700 mb-2">
                Concert Name
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="concertName"
                  value={concertName}
                  onChange={handleInputChange}
                  disabled={createFolderMutation.isPending}
                  placeholder="e.g., summer-concert-2024"
                  className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                    validationError 
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                      : 'border-gray-300 focus:border-blue-500'
                  }`}
                />
                {validationError && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <AlertCircle className="h-5 w-5 text-red-500" />
                  </div>
                )}
              </div>
              
              {/* Validation error message */}
              {validationError && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0" />
                  {validationError}
                </p>
              )}
              
              {/* Helper text */}
              <p className="mt-2 text-sm text-gray-500">
                Folder will be created as: <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">/upload/{concertName || '[concert-name]'}</code>
              </p>
            </div>

            {/* Action buttons */}
            <div className="flex items-center justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                disabled={createFolderMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createFolderMutation.isPending || !concertName.trim()}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createFolderMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Folder className="w-4 h-4 mr-2" />
                    Create Folder
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};